import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronDown, Ruler } from 'lucide-react';

// ── Presets ───────────────────────────────────────────────────────────────────
const PRESETS = {
  women: {
    label: "Women's Standard (cm)",
    data: {
      XS: { chest: 82, waist: 66, hips: 88 },
      S:  { chest: 88, waist: 72, hips: 94 },
      M:  { chest: 94, waist: 78, hips: 100 },
      L:  { chest: 100, waist: 84, hips: 106 },
      XL: { chest: 106, waist: 90, hips: 112 },
      XXL:{ chest: 112, waist: 96, hips: 118 },
    }
  },
  men: {
    label: "Men's Standard (cm)",
    data: {
      S:   { chest: 86, waist: 74, hips: 86 },
      M:   { chest: 92, waist: 80, hips: 92 },
      L:   { chest: 98, waist: 86, hips: 98 },
      XL:  { chest: 104, waist: 92, hips: 104 },
      XXL: { chest: 110, waist: 98, hips: 110 },
    }
  },
  women_bottom: {
    label: "Women's Bottom Wear (cm)",
    data: {
      XS:  { waist: 66, hips: 88,  thigh: 50, inseam: 74, length: 100 },
      S:   { waist: 72, hips: 94,  thigh: 53, inseam: 75, length: 101 },
      M:   { waist: 78, hips: 100, thigh: 56, inseam: 76, length: 102 },
      L:   { waist: 84, hips: 106, thigh: 59, inseam: 76, length: 103 },
      XL:  { waist: 90, hips: 112, thigh: 62, inseam: 77, length: 104 },
      XXL: { waist: 96, hips: 118, thigh: 65, inseam: 77, length: 105 },
    }
  },
  men_bottom: {
    label: "Men's Bottom Wear (cm)",
    data: {
      S:   { waist: 76,  hips: 92,  thigh: 56, inseam: 80, length: 105 },
      M:   { waist: 82,  hips: 98,  thigh: 59, inseam: 80, length: 106 },
      L:   { waist: 88,  hips: 104, thigh: 62, inseam: 81, length: 107 },
      XL:  { waist: 94,  hips: 110, thigh: 65, inseam: 81, length: 108 },
      XXL: { waist: 100, hips: 116, thigh: 68, inseam: 82, length: 109 },
    }
  },
  ethnic: {
    label: 'Ethnic Wear (cm)',
    data: {
      S:   { chest: 86, waist: 70, hips: 90, length: 52 },
      M:   { chest: 92, waist: 76, hips: 96, length: 54 },
      L:   { chest: 98, waist: 82, hips: 102, length: 56 },
      XL:  { chest: 104, waist: 88, hips: 108, length: 58 },
      XXL: { chest: 110, waist: 94, hips: 114, length: 60 },
    }
  },
};

const SIZE_OPTIONS = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '28', '30', '32', '34', '36', '38', '40', '42'];
const MEASUREMENT_OPTIONS = ['chest', 'waist', 'hips', 'shoulder', 'length', 'sleeve', 'inseam', 'thigh'];
const CM_TO_IN = 1 / 2.54;
const IN_TO_CM = 2.54;
const round1 = (n) => Math.round(n * 10) / 10;

// ── Helpers ───────────────────────────────────────────────────────────────────
function parseChartValue(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return null; }
}

function getUnit(chartObj) {
  return chartObj && chartObj._unit === 'in' ? 'in' : 'cm';
}

function toRows(chartObj) {
  if (!chartObj || typeof chartObj !== 'object') return [];
  return Object.keys(chartObj).filter(k => k !== '_unit');
}

function toCols(chartObj) {
  if (!chartObj || typeof chartObj !== 'object') return ['chest', 'waist', 'hips'];
  const colSet = new Set();
  Object.entries(chartObj).forEach(([k, row]) => {
    if (k === '_unit') return;
    if (row && typeof row === 'object') Object.keys(row).forEach(c => colSet.add(c));
  });
  return colSet.size ? Array.from(colSet) : ['chest', 'waist', 'hips'];
}

function convertChartValues(chart, factor) {
  if (!chart || typeof chart !== 'object') return chart;
  const out = {};
  Object.entries(chart).forEach(([k, row]) => {
    if (k === '_unit') return;
    if (!row || typeof row !== 'object') return;
    const newRow = {};
    Object.entries(row).forEach(([m, v]) => {
      if (typeof v === 'number' && Number.isFinite(v)) {
        newRow[m] = round1(v * factor);
      } else {
        newRow[m] = v;
      }
    });
    out[k] = newRow;
  });
  return out;
}

// ── Main Component ────────────────────────────────────────────────────────────
/**
 * SizeChartEditor
 *
 * Props:
 *   value        – current size_chart value (null | dict | legacy string)
 *   onChange     – called with the new value (null | dict)
 *   disabled     – read-only mode
 */
const SizeChartEditor = ({ value, onChange, disabled = false }) => {
  const parsed = parseChartValue(value);
  const [chart, setChart] = useState(parsed || null);
  const [unit, setUnit] = useState(getUnit(parsed));
  const [newSize, setNewSize] = useState('');
  const [newMeasure, setNewMeasure] = useState('');
  const [showPresets, setShowPresets] = useState(false);
  const [customSize, setCustomSize] = useState('');
  const [customMeasure, setCustomMeasure] = useState('');

  // Keep internal state in sync when value changes externally
  useEffect(() => {
    const p = parseChartValue(value);
    setChart(p || null);
    setUnit(getUnit(p));
  }, [value]);

  const emit = (next) => {
    // Always tag the chart with its unit so it round-trips correctly. Null
    // means "no chart" — don't synthesise an object.
    let toEmit = next;
    if (next && typeof next === 'object' && Object.keys(next).filter(k => k !== '_unit').length > 0) {
      toEmit = { ...next, _unit: unit };
    } else if (next && typeof next === 'object') {
      toEmit = null;
    }
    setChart(toEmit);
    onChange(toEmit);
  };

  const rows = toRows(chart);
  const cols = toCols(chart);
  const hasChart = chart && rows.length > 0;

  // ── Actions ──────────────────────────────────────────────────────────────
  const addSize = (sizeLabel) => {
    const label = (sizeLabel || customSize).trim().toUpperCase();
    if (!label || (chart && chart[label])) return;
    const emptyRow = {};
    cols.forEach(c => { emptyRow[c] = ''; });
    emit({ ...(chart || {}), [label]: emptyRow });
    setNewSize('');
    setCustomSize('');
  };

  const removeSize = (sizeLabel) => {
    const next = { ...chart };
    delete next[sizeLabel];
    emit(Object.keys(next).length ? next : null);
  };

  const addMeasurement = (measureLabel) => {
    const label = (measureLabel || customMeasure).trim().toLowerCase();
    if (!label || cols.includes(label)) return;
    const next = {};
    rows.forEach(r => { next[r] = { ...chart[r], [label]: '' }; });
    emit(next);
    setNewMeasure('');
    setCustomMeasure('');
  };

  const removeMeasurement = (measureLabel) => {
    const next = {};
    rows.forEach(r => {
      const rowCopy = { ...chart[r] };
      delete rowCopy[measureLabel];
      next[r] = rowCopy;
    });
    emit(next);
  };

  const setCellValue = (size, measure, val) => {
    const numVal = val === '' ? '' : parseFloat(val);
    emit({
      ...chart,
      [size]: { ...chart[size], [measure]: numVal }
    });
  };

  const applyPreset = (key) => {
    let data = PRESETS[key].data;
    if (unit === 'in') data = convertChartValues(data, CM_TO_IN);
    emit(data);
    setShowPresets(false);
  };

  const toggleUnit = () => {
    const newUnit = unit === 'cm' ? 'in' : 'cm';
    if (chart && rows.length > 0) {
      const factor = newUnit === 'in' ? CM_TO_IN : IN_TO_CM;
      const converted = convertChartValues(chart, factor);
      // Set unit BEFORE emit so the next emit tags the chart correctly.
      setUnit(newUnit);
      // Emit directly (can't rely on emit's `unit` closure that lags one render)
      const tagged = { ...converted, _unit: newUnit };
      setChart(tagged);
      onChange(tagged);
    } else {
      setUnit(newUnit);
    }
  };

  const clearChart = () => emit(null);

  // ── Render ────────────────────────────────────────────────────────────────
  if (disabled) {
    if (!hasChart) return <span className="text-slate-400 text-sm">No size chart</span>;
    return (
      <div className="overflow-x-auto">
        <table className="text-sm border-collapse w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="px-3 py-2 text-left text-slate-600 font-medium border border-slate-200 w-16">Size</th>
              {cols.map(c => (
                <th key={c} className="px-3 py-2 text-center text-slate-600 font-medium border border-slate-200 capitalize">{c} ({unit})</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(size => (
              <tr key={size} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-semibold text-slate-900 border border-slate-200 text-center">{size}</td>
                {cols.map(c => (
                  <td key={c} className="px-3 py-2 text-center text-slate-700 border border-slate-200">
                    {chart[size]?.[c] !== undefined && chart[size]?.[c] !== '' ? chart[size][c] : '–'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="size-chart-editor">
      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Ruler className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">
            {hasChart ? `${rows.length} sizes · ${cols.length} measurements` : 'No size chart added'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Unit toggle (cm ↔ in) */}
          <div
            className="inline-flex items-center bg-slate-100 rounded-lg p-0.5"
            data-testid="size-chart-unit-toggle"
          >
            <button
              type="button"
              onClick={() => unit !== 'cm' && toggleUnit()}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                unit === 'cm' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="size-chart-unit-cm"
              aria-pressed={unit === 'cm'}
            >
              cm
            </button>
            <button
              type="button"
              onClick={() => unit !== 'in' && toggleUnit()}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
                unit === 'in' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="size-chart-unit-in"
              aria-pressed={unit === 'in'}
            >
              in
            </button>
          </div>
          {/* Preset picker */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowPresets(v => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
              data-testid="size-chart-preset-btn"
            >
              Use Preset <ChevronDown className="w-3 h-3" />
            </button>
            {showPresets && (
              <div className="absolute right-0 mt-1 w-52 bg-white border border-slate-200 rounded-xl shadow-lg z-20">
                {Object.entries(PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => applyPreset(key)}
                    className="block w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 first:rounded-t-xl last:rounded-b-xl"
                    data-testid={`preset-${key}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {hasChart && (
            <button
              type="button"
              onClick={clearChart}
              className="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
              data-testid="size-chart-clear-btn"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      {hasChart && (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="text-sm w-full border-collapse">
            <thead>
              <tr className="bg-slate-50">
                <th className="px-3 py-2 text-left text-slate-600 font-medium border-b border-r border-slate-200 w-20 sticky left-0 bg-slate-50">
                  Size
                </th>
                {cols.map(c => (
                  <th key={c} className="px-3 py-2 text-center text-slate-600 font-medium border-b border-r border-slate-200 min-w-[90px]">
                    <div className="flex items-center justify-center gap-1">
                      <span className="capitalize">{c} ({unit})</span>
                      <button
                        type="button"
                        onClick={() => removeMeasurement(c)}
                        className="text-slate-300 hover:text-red-500 transition-colors"
                        title={`Remove ${c}`}
                        data-testid={`remove-measurement-${c}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </th>
                ))}
                {/* Add measurement column */}
                <th className="px-2 py-2 border-b border-slate-200 min-w-[130px]">
                  <MeasurementAdder
                    existing={cols}
                    options={MEASUREMENT_OPTIONS}
                    custom={customMeasure}
                    onCustomChange={setCustomMeasure}
                    onAdd={addMeasurement}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((size, idx) => (
                <tr key={size} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                  <td className="px-3 py-1.5 border-r border-slate-200 sticky left-0 bg-inherit">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-800">{size}</span>
                      <button
                        type="button"
                        onClick={() => removeSize(size)}
                        className="text-slate-300 hover:text-red-500 transition-colors"
                        title={`Remove size ${size}`}
                        data-testid={`remove-size-${size}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                  {cols.map(c => (
                    <td key={c} className="px-2 py-1.5 border-r border-slate-200">
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={chart[size]?.[c] ?? ''}
                        onChange={e => setCellValue(size, c, e.target.value)}
                        placeholder="–"
                        className="w-full px-2 py-1 text-center border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 text-sm bg-white"
                        data-testid={`cell-${size}-${c}`}
                      />
                    </td>
                  ))}
                  <td className="px-2 py-1.5" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Size row */}
      <SizeAdder
        existing={rows}
        options={SIZE_OPTIONS}
        custom={customSize}
        onCustomChange={setCustomSize}
        onAdd={addSize}
        hasChart={hasChart}
      />

      {/* Helper text */}
      {hasChart && (
        <p className="text-xs text-slate-400">
          All measurements in {unit === 'cm' ? 'centimetres' : 'inches'}. Leave blank if not applicable.
          The TryOn Studio uses this to recommend sizes after virtual try-on.
        </p>
      )}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const SizeAdder = ({ existing, options, custom, onCustomChange, onAdd, hasChart }) => {
  const [open, setOpen] = useState(false);
  const available = options.filter(o => !existing.includes(o));

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          data-testid="add-size-btn"
        >
          <Plus className="w-3 h-3" />
          {hasChart ? 'Add Size' : 'Add First Size'}
        </button>
        {open && (
          <div className="absolute left-0 mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-20 p-2">
            <div className="flex flex-wrap gap-1 mb-2">
              {available.slice(0, 12).map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={() => { onAdd(s); setOpen(false); }}
                  className="px-2.5 py-1 text-xs bg-slate-100 text-slate-700 rounded-lg hover:bg-indigo-100 hover:text-indigo-700"
                  data-testid={`size-option-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex gap-1">
              <input
                type="text"
                value={custom}
                onChange={e => onCustomChange(e.target.value.toUpperCase())}
                placeholder="Custom…"
                maxLength={8}
                className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-400"
                data-testid="custom-size-input"
              />
              <button
                type="button"
                onClick={() => { if (custom.trim()) { onAdd(); setOpen(false); } }}
                className="px-2 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40"
                disabled={!custom.trim()}
              >
                Add
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MeasurementAdder = ({ existing, options, custom, onCustomChange, onAdd }) => {
  const [open, setOpen] = useState(false);
  const available = options.filter(o => !existing.includes(o));

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-white text-slate-600 border border-dashed border-slate-300 rounded-lg hover:border-indigo-400 hover:text-indigo-600 transition-colors whitespace-nowrap"
        data-testid="add-measurement-btn"
      >
        <Plus className="w-3 h-3" /> Add Column
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-20 p-2">
          <div className="flex flex-wrap gap-1 mb-2">
            {available.map(m => (
              <button
                key={m}
                type="button"
                onClick={() => { onAdd(m); setOpen(false); }}
                className="px-2.5 py-1 text-xs bg-slate-100 text-slate-700 rounded-lg hover:bg-indigo-100 hover:text-indigo-700 capitalize"
                data-testid={`measure-option-${m}`}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            <input
              type="text"
              value={custom}
              onChange={e => onCustomChange(e.target.value.toLowerCase())}
              placeholder="Custom…"
              maxLength={20}
              className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded-lg focus:ring-1 focus:ring-indigo-400"
              data-testid="custom-measure-input"
            />
            <button
              type="button"
              onClick={() => { if (custom.trim()) { onAdd(); setOpen(false); } }}
              disabled={!custom.trim()}
              className="px-2 py-1 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-40"
            >
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SizeChartEditor;
